import { useEffect, useMemo, useState } from 'react'
import { stripDiacritics } from 'utils/string'

/**
 * @param value - field value. string or object
 * @param onSelect - callback, called when user selects item from list
 * @param items - list items. can be filtered
 * @param keyLabel - key of `val` object. uses to filter list
 * @returns
 *  selectProps: props for select component
 *  list - filtered list
 */
export const useSelectList = (value, onSelect, items, keyLabel) => {
  const getValue = item => {
    if (keyLabel) {
      return (item && item[keyLabel]) || ''
    }
    return item || ''
  }

  const [inputValue, setInputValue] = useState(getValue(value))

  useEffect(() => setInputValue(getValue(value)), [value])

  const [filtered, setFiltered] = useState(false)
  const list = useMemo(() => {
    const valLowerCase = ('' + inputValue).trim().toLowerCase()
    if (filtered && valLowerCase) {
      const filterFn = item =>
        getValue(item).toLowerCase().includes(valLowerCase)
      return items.filter(filterFn)
    }

    return items
  }, [inputValue, items, filtered])

  const onChange = e => {
    const val = e.target.value
    setFiltered(true)
    setInputValue(val)
    if (val === '' && onSelect) onSelect(null)
  }

  const onSelectEvent = value => {
    setFiltered(false)
    setInputValue(getValue(value))
    if (onSelect) onSelect(value)
  }

  const handleBlur = () => {
    setInputValue(getValue(value))
    setFiltered(false)
  }

  return {
    selectProps: {
      value: inputValue ?? '',
      onChange,
      onSelect: onSelectEvent,
      onBlur: handleBlur,
    },
    list,
  }
}

export const useSelectListLocalizedName = (
  value,
  onChange,
  items,
  fieldName
) => {
  const getValue = value => value?.[fieldName ?? 'localizedName'] || ''
  const getLocalizeName = value =>
    typeof value === 'string' ? value : getValue(value)

  const [inputValue, setInputValue] = useState(getLocalizeName(value))

  useEffect(() => setInputValue(getLocalizeName(value)), [value])

  const [filtered, setFiltered] = useState(false)
  const list = useMemo(() => {
    const valLowerCase = ('' + inputValue).trim().toLowerCase()
    if (filtered && valLowerCase) {
      const filterFn = item =>
        stripDiacritics(getValue(item)).toLowerCase().includes(valLowerCase)
      return items.filter(filterFn)
    }

    return items
  }, [inputValue, items, filtered])

  const onChangeEvent = e => {
    const val = e.target.value
    setFiltered(true)
    setInputValue(val)
    if (onChange) onChange(val)
  }

  const onSelectEvent = value => {
    setFiltered(false)
    setInputValue(getValue(value))
    if (onChange) onChange(value)
  }

  const handleBlur = () => {
    setFiltered(false)
  }

  return {
    selectProps: {
      value: inputValue ?? '',
      onChange: onChangeEvent,
      onSelect: onSelectEvent,
      onBlur: handleBlur,
    },
    list,
  }
}
